import { Box, Container, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import React from "react";
import i18next from "../lang";

interface FooterProps {
  drawerOpen: boolean;
  isAuthenticated?: boolean;
}

export default function Footer({ drawerOpen, isAuthenticated }: FooterProps) {
  const [privacyOpen, setPrivacyOpen] = React.useState(false);
  const [tosOpen, setTosOpen] = React.useState(false);

  const handleCompanyClick = () => {
    window.open('https://kokugoteki.jp/company/', '_blank');
  };

  return (
    <Box
      component="footer"
      sx={{
        width: { xs: '100%', md: drawerOpen ? "calc(100% - 240px)" : "100%" },
        ml: { xs: 0, md: drawerOpen ? '240px' : 0 },
        mt: 'auto',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s ease',
      }}
    >
      {/* Navigation Bar — 未ログイン時のみ表示 */}
      {!isAuthenticated && (
        <Box sx={{ bgcolor: 'rgba(32, 32, 32, 0.9)', py: 2, px: 2, color: 'white', backdropFilter: 'blur(4px)' }}>
          <Container maxWidth="md">
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              gap: { xs: 1, md: 4 },
              flexWrap: 'wrap'
            }}>
              <Typography
                variant="body2"
                sx={{ cursor: 'pointer', opacity: 0.8, '&:hover': { opacity: 1 }, fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                onClick={handleCompanyClick}
              >
                {i18next.t('nav.company_info')}
              </Typography>
              <Typography
                variant="body2"
                sx={{ cursor: 'pointer', opacity: 0.8, '&:hover': { opacity: 1 }, fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                onClick={() => setPrivacyOpen(true)}
              >
                {i18next.t('nav.privacy_policy')}
              </Typography>
              <Typography
                variant="body2"
                sx={{ cursor: 'pointer', opacity: 0.8, '&:hover': { opacity: 1 }, fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                onClick={() => setTosOpen(true)}
              >
                {i18next.t('nav.terms_of_use')}
              </Typography>
            </Box>
          </Container>
        </Box>
      )}

      {/* Copyright Bar */}
      <Box sx={{
        background: 'linear-gradient(90deg, rgba(230, 243, 245, 0.6) 0%, rgba(212, 238, 242, 0.6) 50%, rgba(191, 233, 239, 0.6) 100%)',
        backdropFilter: 'blur(4px)',
        py: 2,
        borderTop: '1px solid rgba(255,255,255,0.3)'
      }}>
        <Container maxWidth="sm">
          <Typography variant="body2" color="#005580" align="center" sx={{ fontWeight: 500, fontSize: { xs: '0.7rem', md: '0.875rem' } }}>
            ©{new Date().getFullYear()} Tamai Investment Educations Inc. All rights reserved.
          </Typography>
        </Container>
      </Box>

      {/* Privacy Policy Dialog */}
      <Dialog
        open={privacyOpen}
        onClose={() => setPrivacyOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          {i18next.t('nav.privacy_policy')}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" paragraph>
            Tamai Investment Educations Inc.（以下「当社」といいます）は、お客様の個人情報の保護を重要な責務と認識し、以下のプライバシーポリシーに基づき、個人情報を適切に取り扱います。
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            1. 個人情報の収集
          </Typography>
          <Typography variant="body2" paragraph>
            当社は、サービスの提供にあたり、お客様から以下の個人情報を収集することがあります。
            <br />・氏名、住所、電話番号、メールアドレス
            <br />・注文情報、配送先情報
            <br />・お支払い情報
            <br />・その他サービス利用に必要な情報
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            2. 個人情報の利用目的
          </Typography>
          <Typography variant="body2" paragraph>
            収集した個人情報は、以下の目的で利用いたします。
            <br />・商品の発送および関連するサービスの提供
            <br />・ご注文内容の確認および請求書の発行
            <br />・お問い合わせへの対応
            <br />・サービス向上のための分析
            <br />・新商品やキャンペーン情報のご案内（同意いただいた場合）
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            3. 個人情報の第三者提供
          </Typography>
          <Typography variant="body2" paragraph>
            当社は、以下の場合を除き、お客様の個人情報を第三者に提供することはありません。
            <br />・お客様の同意がある場合
            <br />・法令に基づく場合
            <br />・配送業者等、サービス提供に必要な業務委託先への提供
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            4. 個人情報の管理
          </Typography>
          <Typography variant="body2" paragraph>
            当社は、お客様の個人情報を正確かつ最新の状態に保ち、不正アクセス、紛失、破損、改ざん、漏洩などを防止するため、適切なセキュリティ対策を講じます。
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            5. 個人情報の開示・訂正・削除
          </Typography>
          <Typography variant="body2" paragraph>
            お客様ご本人からの個人情報の開示、訂正、削除のご請求には、本人確認の上、合理的な範囲で速やかに対応いたします。
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            6. Cookieの使用
          </Typography>
          <Typography variant="body2" paragraph>
            当社のウェブサイトでは、サービス向上のためCookieを使用することがあります。Cookieの使用を希望されない場合は、ブラウザの設定で無効化できます。
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            7. お問い合わせ
          </Typography>
          <Typography variant="body2" paragraph>
            個人情報の取り扱いに関するお問い合わせは、当社お客様サポート窓口までご連絡ください。
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            制定日: 2024年1月1日
            <br />最終改定日: 2024年1月1日
            <br />Tamai Investment Educations Inc.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrivacyOpen(false)}>{i18next.t('nav.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Terms of Service Dialog */}
      <Dialog
        open={tosOpen}
        onClose={() => setTosOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          {i18next.t('nav.terms_of_use')}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" paragraph>
            この利用規約（以下「本規約」といいます）は、Tamai Investment Educations Inc.（以下「当社」といいます）が提供するECサービス（以下「本サービス」といいます）の利用条件を定めるものです。
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            第1条（適用）
          </Typography>
          <Typography variant="body2" paragraph>
            本規約は、お客様と当社との間の本サービスの利用に関わる一切の関係に適用されます。
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            第2条（利用登録）
          </Typography>
          <Typography variant="body2" paragraph>
            1. 登録希望者が当社の定める方法によって利用登録を申請し、当社がこれを承認することによって、利用登録が完了します。
            <br />2. 当社は、登録申請者に以下の事由があると判断した場合、利用登録の申請を承認しないことがあります。
            <br />・虚偽の事項を届け出た場合
            <br />・本規約に違反したことがある者からの申請である場合
            <br />・その他、当社が利用登録を相当でないと判断した場合
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            第3条（ユーザーIDおよびパスワードの管理）
          </Typography>
          <Typography variant="body2" paragraph>
            1. お客様は、自己の責任においてユーザーIDおよびパスワードを管理するものとします。
            <br />2. ユーザーIDおよびパスワードを第三者に利用させ、または貸与、譲渡、売買等することはできません。
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            第4条（商品の購入）
          </Typography>
          <Typography variant="body2" paragraph>
            1. お客様は、本サービスを通じて当社が販売する商品を購入することができます。
            <br />2. 売買契約は、当社がお客様からの注文を承諾した時点で成立します。
            <br />3. 商品の所有権は、代金の支払いが完了した時点でお客様に移転します。
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            第5条（支払方法）
          </Typography>
          <Typography variant="body2" paragraph>
            お客様は、当社が指定する方法により、商品代金をお支払いいただきます。請求書払いの場合、当社が定める期日までにお支払いください。
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            第6条（配送）
          </Typography>
          <Typography variant="body2" paragraph>
            1. 当社は、お客様が指定した配送先に商品を配送します。
            <br />2. 配送日時はあくまで目安であり、交通事情等により遅延する場合があります。
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            第7条（返品・交換）
          </Typography>
          <Typography variant="body2" paragraph>
            1. 商品の返品・交換は、商品到着後7日以内に当社にご連絡いただいた場合に限り、対応いたします。
            <br />2. お客様のご都合による返品・交換の場合、送料はお客様のご負担となります。
            <br />3. 商品の性質上、返品・交換をお受けできない場合があります。
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            第8条（禁止事項）
          </Typography>
          <Typography variant="body2" paragraph>
            お客様は、本サービスの利用にあたり、以下の行為をしてはなりません。
            <br />・法令または公序良俗に違反する行為
            <br />・当社または第三者の知的財産権を侵害する行為
            <br />・サービスの運営を妨害する行為
            <br />・不正アクセスまたはこれを試みる行為
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            第9条（免責事項）
          </Typography>
          <Typography variant="body2" paragraph>
            1. 当社は、本サービスに事実上または法律上の瑕疵がないことを明示的にも黙示的にも保証しておりません。
            <br />2. 当社は、お客様に生じた損害について、当社の故意または重過失による場合を除き、責任を負いません。
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            第10条（規約の変更）
          </Typography>
          <Typography variant="body2" paragraph>
            当社は、必要と判断した場合には、お客様に通知することなく本規約を変更できるものとします。変更後の規約は、本サービス上に表示した時点から効力を生じます。
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            第11条（準拠法・裁判管轄）
          </Typography>
          <Typography variant="body2" paragraph>
            本規約の解釈にあたっては、日本法を準拠法とします。本サービスに関して紛争が生じた場合には、東京地方裁判所を第一審の専属的合意管轄とします。
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            制定日: 2024年1月1日
            <br />最終改定日: 2024年1月1日
            <br />Tamai Investment Educations Inc.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTosOpen(false)}>{i18next.t('nav.close')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
